import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";

import { SideNavPageLayout } from "shared/components";
import { useBoundStore } from "store";
import { useOrganizations } from "organizations/queries";
import { useOrganizationUser } from "users/queries";
import { buildOrganizationsPath } from "shared/utils/paths";

const titleMap: Record<string, string> = {
  dashboard: "Dashboard",
  projects: "Projects",
  settings: "Organization Settings",
  eligibility: "Eligibility Checker",
};

export const OrganizationLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, organizationId } = useParams();
  const lastSegment = location.pathname.split("/").filter(Boolean).pop() ?? "";
  const title = titleMap[lastSegment] ?? "Dashboard";
  const {
    organizations,
    setOrganizations,
    currentOrganization,
    setCurrentOrganization,
    setUser,
    user,
    tenant,
  } = useBoundStore();

  // Use auth0UserId from store
  const userId = user?.userId || user?.auth0UserId;

  // Only fetch if organizations are not in store
  const { data: orgsData } = useOrganizations(tenant?.id, {
    enabled: !!tenant?.id && !organizations?.length,
  });

  const { data: organizationUser } = useOrganizationUser(
    userId,
    organizationId,
    {
      enabled: !!userId && !!organizationId,
    }
  );

  // keep organizations in store
  useEffect(() => {
    if (orgsData) {
      setOrganizations(orgsData);
    }
  }, [orgsData, setOrganizations]);

  // If orgsData from query is not available but we need it, use store data
  const effectiveOrganizations = orgsData || organizations;

  // ensure current organization matches route
  useEffect(() => {
    if (!effectiveOrganizations?.length || !organizationId) return;
    const matched = effectiveOrganizations.find((o) => o.id === organizationId);

    if (matched) {
      if (matched.id !== currentOrganization?.id) {
        setCurrentOrganization(matched);
      }

      if (matched.tenantId !== tenantId) {
        navigate(buildOrganizationsPath(tenantId), { replace: true });
      }
    } else {
      // unknown org in route -> send to org list
      navigate(buildOrganizationsPath(tenantId), { replace: true });
    }
  }, [currentOrganization?.id, navigate, organizationId, effectiveOrganizations, setCurrentOrganization, tenantId]);

  // Merge organization user data into the user object when it changes
  useEffect(() => {
    if (!organizationUser) return;

    setUser({
      ...organizationUser,
    });
  }, [organizationUser, setUser]);

  return (
    <SideNavPageLayout title={title}>
      <Outlet />
    </SideNavPageLayout>
  );
};
